import React from "react";
import { WaiterModel, WaiterSharedProps } from "./types";
import { useTranslation } from "react-i18next";
import CloseOrderDrawer from "../components/tables/CloseOrderDrawer";
import { ConfirmWaiterCallButton } from "./ConfirmWaiterCallButton";

interface Props extends WaiterSharedProps {
  tableId: string;
  sectionId: string;
  waiterModel: WaiterModel | undefined;
}

const fmt = (n: number) => n.toLocaleString("tr-TR");

const WaiterTableDetail: React.FC<Props> = ({
  tableId,
  sectionId,
  sections,
  allTables,
  orderStateList,
  waiterCallList,
  orderProductStatusList,
  updateOrderProductStatus,
  navigate,
  waiterModel,
}) => {
  const table = allTables.find((t) => t.id === tableId);
  const section = sections.find((s) => s.id === sectionId);
  const os = orderStateList.find((o) => o.order.tableId === tableId);
  const calls = waiterCallList.filter((w) => w.tableId === tableId);
  const { t } = useTranslation();
  if (!table) {
    return (
      <div
        style={{
          fontFamily: "'Nunito',sans-serif",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100%",
          background: "#FAFAF9",
        }}
      >
        <div style={{ color: "#A8A29E", fontWeight: 800 }}>
          Masa bulunamadı.
        </div>
      </div>
    );
  }

  const products = os?.orderProductList ?? [];
  const subtotal = products
    .filter((op) => op.status.name === "delivered")
    .reduce((s, op) => {
      return s + op.menuProductPrice * op.amount;
    }, 0);

  const total = subtotal;

  // Ürün durum grupları (OrderProductStatusModel.text ile)
  const preparing = products.filter(
    (op) => op.status.text === "Hazırlanıyor" || op.status.name === "preparing",
  ).length;
  const ready = products.filter(
    (op) => op.status.text === "Hazır" || op.status.name === "ready",
  ).length;
  const served = products.filter(
    (op) =>
      op.status.text === "Servis Edildi" || op.status.name === "delivered",
  ).length;

  // Durum değiştirme
  const handleStatusChange = async (
    orderProductId: string,
    statusId: number,
  ) => {
    await updateOrderProductStatus(tableId, orderProductId, statusId);
  };

  return (
    <div
      style={{
        fontFamily: "'Nunito', sans-serif",
        background: "#FAFAF9",
        minHeight: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <div
        style={{
          background: "#1C1917",
          color: "#fff",
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          gap: 12,
          position: "sticky",
          top: 0,
          zIndex: 50,
        }}
      >
        <button
          onClick={() => navigate({ name: "WaiterTables", sectionId })}
          style={{
            width: 36,
            height: 36,
            background: "#292524",
            border: "1px solid #44403C",
            borderRadius: 12,
            color: "#fff",
            fontSize: 16,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          ←
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 14 }}>
            {section?.name} · {table.name}
          </div>
          <div style={{ fontSize: 11, color: "#A8A29E", fontWeight: 600 }}>
            Sipariş No: {os?.order.number || "–"} ·{" "}
            {os?.order.mobilePhone || ""}
          </div>
        </div>
        {calls.length > 0 && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 900,
              background: "#F59E0B",
              color: "#1C1917",
              padding: "5px 12px",
              borderRadius: 20,
            }}
          >
            ⚡ {calls.length} Çağrı
          </span>
        )}
      </div>

      {/* Durum özetleri */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 8,
          padding: "12px 16px",
          background: "#fff",
          borderBottom: "1px solid #E7E5E4",
        }}
      >
        {[
          {
            val: preparing,
            label: "Hazırlanıyor",
            bg: "#F5F3FF",
            color: "#7C3AED",
          },
          {
            val: ready,
            label: "Servis Bekliyor",
            bg: "#ECFDF5",
            color: "#059669",
          },
          {
            val: served,
            label: "Servis Edildi",
            bg: "#F5F5F4",
            color: "#78716C",
          },
        ].map((p, i) => (
          <div
            key={i}
            style={{
              background: p.bg,
              borderRadius: 12,
              padding: "10px 6px",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 900, color: p.color }}>
              {p.val}
            </div>
            <div
              style={{
                fontSize: 9,
                fontWeight: 800,
                color: p.color,
                opacity: 0.85,
                marginTop: 2,
              }}
            >
              {p.label}
            </div>
          </div>
        ))}
      </div>

      {/* Ürün listesi */}
      <div style={{ flex: 1, overflowY: "auto", padding: "12px 16px 0" }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 900,
            color: "#A8A29E",
            textTransform: "uppercase",
            letterSpacing: 1,
            marginBottom: 8,
          }}
        >
          Siparişler ({products.length} kalem)
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            paddingBottom: 12,
          }}
        >
          {products.length === 0 && (
            <div
              style={{
                textAlign: "center",
                padding: "40px 0",
                color: "#A8A29E",
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              Henüz sipariş yok.
            </div>
          )}
          {products.map((op) => (
            <div
              key={op.id}
              style={{
                background: "#fff",
                borderRadius: 14,
                border: "1px solid #E7E5E4",
                padding: "12px 14px",
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
              }}
            >
              {/* Fotoğraf */}
              {op.menuProductPhoto && (
                <img
                  src={
                    (op.menuProductPhoto as any).toApiImage?.() ??
                    op.menuProductPhoto
                  }
                  alt={op.menuProductName}
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    objectFit: "cover",
                    flexShrink: 0,
                  }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              )}
              {/* Adet badge */}
              {!op.menuProductPhoto && (
                <div
                  style={{
                    width: 34,
                    height: 34,
                    background: "#F5F5F4",
                    borderRadius: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 900,
                    fontSize: 14,
                    color: "#1C1917",
                    flexShrink: 0,
                  }}
                >
                  {op.amount}
                </div>
              )}

              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <span
                    style={{ fontWeight: 800, fontSize: 13, color: "#1C1917" }}
                  >
                    {op.menuProductName}
                    {op.menuProductPhoto && (
                      <span
                        style={{
                          fontSize: 11,
                          color: "#A8A29E",
                          fontWeight: 600,
                        }}
                      >
                        ×{op.amount}
                      </span>
                    )}
                  </span>
                  <span
                    style={{
                      fontWeight: 900,
                      fontSize: 14,
                      color: "#1C1917",
                      flexShrink: 0,
                      marginLeft: 8,
                    }}
                  >
                    ₺{fmt(op.menuProductPrice * op.amount)}
                  </span>
                </div>
                {op.orderNote && (
                  <div
                    style={{
                      fontSize: 11,
                      color: "#A8A29E",
                      fontWeight: 600,
                      marginTop: 2,
                    }}
                  >
                    📝 {op.orderNote}
                  </div>
                )}
                {/* Durum seçici – OrderProductStatusModel listesinden */}
                <div
                  style={{
                    display: "flex",
                    gap: 5,
                    flexWrap: "wrap",
                    marginTop: 6,
                  }}
                >
                  {orderProductStatusList.map((st) => {
                    const isActive = op.status.id === st.id;
                    return (
                      <button
                        key={st.id}
                        onClick={() => handleStatusChange(op.id, st.id)}
                        style={{
                          fontSize: 10,
                          fontWeight: 800,
                          padding: "3px 10px",
                          borderRadius: 20,
                          cursor: "pointer",
                          border: "1px solid",
                          background: isActive ? st.color : "transparent",
                          color: isActive ? "#fff" : st.color,
                          borderColor: st.color,
                          opacity: isActive ? 1 : 0.6,
                        }}
                      >
                        {t(`common.${st.name}`)}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Hesap özeti */}
      <div
        style={{
          background: "#fff",
          borderTop: "1px solid #E7E5E4",
          padding: "14px 16px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 5,
            marginBottom: 12,
          }}
        >
          {[["Ara Toplam", `₺${fmt(subtotal)}`]].map(([l, v]) => (
            <div
              key={l}
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 13,
                color: "#78716C",
                fontWeight: 600,
              }}
            >
              <span>{l}</span>
              <span>{v}</span>
            </div>
          ))}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              paddingTop: 8,
              borderTop: "1px dashed #E7E5E4",
            }}
          >
            <span style={{ fontWeight: 900, fontSize: 15, color: "#1C1917" }}>
              Genel Toplam
            </span>
            <span style={{ fontWeight: 900, fontSize: 18, color: "#1C1917" }}>
              ₺{fmt(total)}
            </span>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 8,
          }}
        >
          <button
            onClick={() => navigate({ name: "WaiterTables", sectionId })}
            style={{
              background: "#F5F5F4",
              color: "#1C1917",
              fontWeight: 800,
              fontSize: 13,
              padding: "12px 0",
              borderRadius: 12,
              border: "none",
              cursor: "pointer",
            }}
          >
            Geri
          </button>
          <ConfirmWaiterCallButton
            tableId={tableId}
            waiterModel={waiterModel}
          />
          <CloseOrderDrawer
            order={orderStateList.find((x) => x.order.tableId === tableId)}
          />
        </div>
      </div>
    </div>
  );
};

export default WaiterTableDetail;
